/**
 * seoBrand.js — Brand keyword auto-suffix helpers.
 *
 * Product direction: every public-facing page on japantollreceipts.jp
 * must consistently advertise the highest-volume Japanese ETC search
 * phrase — "ETC利用照会サービス" / "ETC Usage Inquiry Service" / "MEISAI" —
 * so Google, Bing, and Yahoo Japan recognise JTR as the email-reports
 * infrastructure for that service. We are NOT the official ETC利用照会
 * サービス. The suffix phrasing ("メール配信" / "Email Reports")
 * positions JTR as the independent emailed-reports layer.
 *
 * Usage:
 *   import { withBrandSuffix, withBrandDesc } from '@/lib/seoBrand';
 *   document.title = withBrandSuffix(myTitle, ja);
 *   setMeta('description', withBrandDesc(myDesc, ja));
 *
 * The helpers are idempotent — if the source string already mentions
 * "ETC利用照会サービス", "ETC Usage Inquiry Service", or "MEISAI",
 * the suffix is not re-applied (so we never produce duplicate phrasing
 * or absurdly long titles on auto-rerender).
 */

const BRAND_PROBES = ['ETC利用照会サービス', 'ETC Usage Inquiry Service', 'MEISAI'];

// Hard cap on the rendered `<title>` length. Browsers + Google SERP
// truncate around 60–70 chars; we use 80 as a generous-but-defended
// upper bound so dynamic suffixes never push us past Google's display
// width. Roadmap / changelog pages also enforce 80 internally.
const TITLE_MAX = 80;

const TITLE_SUFFIX_JA = '｜ETC利用照会サービス連携メール配信';
const TITLE_SUFFIX_EN = ' | ETC Usage Inquiry Service Email Reports';

const DESC_SUFFIX_JA =
  ' 本サービスはETC利用照会サービス（MEISAI）と連携し、ETC利用明細・ETC領収書をメールで自動配信する独立運営サービスです。';
const DESC_SUFFIX_EN =
  ' Japan Toll Receipts connects to the official ETC Usage Inquiry Service (ETC利用照会サービス / MEISAI) to deliver ETC usage statements and receipts by email.';

const KW_SUFFIX_JA =
  'ETC利用照会サービス,ETC利用照会サービス 使い方,ETC利用照会サービス 登録,ETC利用照会サービス ログイン,MEISAI';
const KW_SUFFIX_EN =
  'ETC Usage Inquiry Service,ETC Usage Inquiry Service login,ETC Usage Inquiry Service registration,MEISAI,ETC利用照会サービス';

export function mentionsBrand(s) {
  return !!s && BRAND_PROBES.some((p) => s.includes(p));
}

/**
 * withBrandSuffix(title, ja)
 *   - If the title already mentions the brand: return as-is (no double).
 *   - If appending the suffix would exceed TITLE_MAX (80) chars: return
 *     the title unchanged — the brand is still strongly represented on
 *     every page via the footer, meta description, and index.html
 *     static HTML, so we never sacrifice the 80-char budget for keyword
 *     density inside <title>.
 *   - If the base title is already over TITLE_MAX, return it as-is —
 *     trimming would silently lose meaning. Pages with over-long base
 *     titles must be edited individually.
 */
export function withBrandSuffix(title, ja) {
  if (!title) return title;
  if (mentionsBrand(title)) return title;
  if (title.length > TITLE_MAX) return title;
  const suffix = ja ? TITLE_SUFFIX_JA : TITLE_SUFFIX_EN;
  if (title.length + suffix.length > TITLE_MAX) return title;
  return title + suffix;
}

export function withBrandDesc(desc, ja) {
  if (!desc) return desc;
  if (mentionsBrand(desc)) return desc;
  return desc + (ja ? DESC_SUFFIX_JA : DESC_SUFFIX_EN);
}

export function withBrandKeywords(keywords, ja) {
  const extra = ja ? KW_SUFFIX_JA : KW_SUFFIX_EN;
  if (!keywords) return extra;
  if (mentionsBrand(keywords)) return keywords;
  return keywords + ',' + extra;
}
